import React from 'react';
import Icon from './icons';
import { useSch } from './context';
import { HISTORY_DAYS, canRepeatNextYear, dayDiff, inRange, localParts, ymdParts } from '../../../schedule/model';

export function ConfirmBox({ onNo, onYes }) {
  const { t } = useSch();
  return (
    <div className="sch6-confirm" role="alert">
      <span>{t('sch6.confirm_del')}</span>
      <span style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="sch6-btn" onClick={onNo}>{t('sch6.cancel')}</button>
        <button type="button" className="sch6-btn sch6-danger" onClick={onYes} autoFocus>{t('sch6.delete')}</button>
      </span>
    </div>
  );
}

function EventRow({ e, confirm, onEdit, onDelete, onRepeat, onNo, onYes }) {
  const { t, f, now, year, showName, whereText } = useSch();
  const days = dayDiff(e.start, e.end) + 1;
  const live = e.start <= now.date && e.end >= now.date && (e.allDay || !e.once || inRange(e.from, e.to, now.min));
  const left = dayDiff(now.date, e.start);
  const when = e.start === e.end ? f.dtxt(e.start, true, year) : t('sch6.range_from_to', { a: f.dtxt(e.start, true, year), b: f.dtxt(e.end, true, year) });
  let badge;
  if (live) badge = <span className="sch6-badge sch6-ok">{t('sch6.live_until', { when: e.once ? (e.to === 1440 ? t('sch6.live_until_eod') : f.tm(e.to)) : (e.end === now.date ? t('sch6.ends_today') : f.dtxt(e.end, true, year)) })}</span>;
  else if (left === 0) badge = <span className="sch6-badge">{t('sch6.starts_today_at', { time: f.tm(e.from) })}</span>;
  else badge = <span className="sch6-badge">{f.startsInTxt(left)}</span>;
  if (e.once) badge = <><span className="sch6-badge sch6-mute">{t('sch6.once_badge')}</span>{' '}{badge}</>;
  const m = ymdParts(e.start);
  return (
    <>
      <div className="sch6-card sch6-evitem">
        <div className="sch6-cal"><div className="sch6-m">{f.monthName(m.m - 1)}</div><div className="sch6-d sch6-num">{f.num(m.d)}</div></div>
        <div className="sch6-t" style={{ minWidth: 0 }}>
          <b>{e.name}</b>{' '}{badge}
          <div className="sch6-s" style={{ color: 'var(--muted)', fontSize: 13.5, marginTop: 2 }}>
            {`${t('sch6.shows')} `}<strong style={{ color: 'var(--ink)' }}>{showName(e.show)}</strong>{` · ${when}${days > 1 ? ` ${f.daysCountTxt(days)}` : ''} · ${e.allDay ? t('sch6.all_day') : f.rangeTxt(e.from, e.to)} · ${whereText(e)}`}
          </div>
        </div>
        <div className="sch6-ctrl">
          {!e.once && canRepeatNextYear(e, now.date) && (
            <button type="button" className="sch6-btn sch6-ghost sch6-btn-sm" onClick={() => onRepeat(e)} aria-label={t('sch6.repeat_aria', { name: e.name })}>
              <Icon name="repeat" />{t('sch6.repeat')}
            </button>
          )}
          <button type="button" className="sch6-icon-btn" onClick={() => onEdit(e)} aria-label={t('sch6.edit_aria', { name: e.name })}><Icon name="edit" /></button>
          <button type="button" className="sch6-icon-btn" onClick={() => onDelete(e)} aria-label={t('sch6.delete_aria', { name: e.name })}><Icon name="trash" /></button>
        </div>
      </div>
      {confirm && <ConfirmBox onNo={onNo} onYes={onYes} />}
    </>
  );
}

/** One ended show / event of the history: dimmed, with how it ended, «use again» and delete. */
function PastRow({ e, confirm, onReuse, onDelete, onNo, onYes }) {
  const { t, f, now, year, showName, whereText, tzOf } = useSch();
  const days = dayDiff(e.start, e.end) + 1;
  const when = e.start === e.end ? f.dtxt(e.start, true, year) : t('sch6.range_from_to', { a: f.dtxt(e.start, true, year), b: f.dtxt(e.end, true, year) });
  const m = ymdParts(e.start);
  const early = e.endedEarlyMs != null;
  const status = early
    ? <span className="sch6-badge sch6-warn">{t('sch6.past_early')}{' '}{t('sch6.past_early_at', { time: f.tm(localParts(e.endedEarlyMs, tzOf(e.where.ids[0]) || undefined).min) })}</span>
    : <span className="sch6-badge sch6-mute">{t('sch6.past_done')}</span>;
  return (
    <>
      <div className="sch6-card sch6-evitem sch6-past">
        <div className="sch6-cal"><div className="sch6-m">{f.monthName(m.m - 1)}</div><div className="sch6-d sch6-num">{f.num(m.d)}</div></div>
        <div className="sch6-t" style={{ minWidth: 0 }}>
          <b>{e.name}</b>{' '}{e.once && <><span className="sch6-badge sch6-mute">{t('sch6.once_badge')}</span>{' '}</>}{status}
          <div className="sch6-s" style={{ color: 'var(--muted)', fontSize: 13.5, marginTop: 2 }}>
            {`${t('sch6.past_shown')} `}<strong style={{ color: 'var(--ink)' }}>{showName(e.show)}</strong>{` · ${when}${days > 1 ? ` ${f.daysCountTxt(days)}` : ''} · ${e.allDay ? t('sch6.all_day') : f.rangeTxt(e.from, e.to)} · ${whereText(e)}`}
          </div>
        </div>
        <div className="sch6-ctrl">
          <button type="button" className="sch6-btn" onClick={() => onReuse(e)} aria-label={t('sch6.reuse_aria', { name: e.name })}><Icon name="repeat" />{t('sch6.reuse')}</button>
          <button type="button" className="sch6-icon-btn" onClick={() => onDelete(e)} aria-label={t('sch6.delete_hist_aria', { name: e.name })}><Icon name="trash" /></button>
        </div>
      </div>
      {confirm && <ConfirmBox onNo={onNo} onYes={onYes} />}
    </>
  );
}

/** «السابقة · N»: collapsed by default, the last 90 days newest first (the prototype's `pastHtml()`). */
function PastSection({ past, open, onToggle, confirmDel, onReuse, onDelete, onNo, onYes }) {
  const { t, f } = useSch();
  if (!past.length) return null;
  return (
    <>
      <button type="button" className="sch6-pasthead" aria-expanded={open} aria-controls="sch6-pastlist" onClick={onToggle}>
        <span><Icon name="clock" />{` ${t('sch6.past_head', { n: f.num(past.length) })}`}</span>
        <Icon name="chev" className="sch6-chev" />
      </button>
      {open && (
        <div id="sch6-pastlist" style={{ display: 'grid', gap: 10 }}>
          <p className="sch6-pastnote">{t('sch6.past_note', { n: f.num(HISTORY_DAYS) })}</p>
          {past.map((e) => (
            <PastRow key={e.id} e={e} confirm={confirmDel && confirmDel.id === e.id} onReuse={onReuse} onDelete={onDelete} onNo={onNo} onYes={onYes} />
          ))}
        </div>
      )}
    </>
  );
}

/** «المناسبات»: upcoming and live events of the target, then the collapsed history «السابقة». */
export default function EventsList({ upcoming, past, pastOpen, onPastToggle, confirmDel, onAdd, onEdit, onDelete, onRepeat, onReuse, onNo, onYes }) {
  const { t } = useSch();
  return (
    <section className="sch6-list" aria-labelledby="sch6-evh">
      <div className="sch6-sechead"><h2 id="sch6-evh">{t('sch6.events')}</h2><p>{t('sch6.events_sub')}</p></div>
      {!upcoming.length && (
        <div className="sch6-empty-row">{t('sch6.events_empty')}
          <button type="button" className="sch6-btn" onClick={onAdd}><Icon name="plus" />{t('sch6.add_event')}</button>
        </div>
      )}
      {upcoming.map((e) => (
        <EventRow key={e.id} e={e} confirm={confirmDel && confirmDel.id === e.id} onEdit={onEdit} onDelete={onDelete} onRepeat={onRepeat} onNo={onNo} onYes={onYes} />
      ))}
      <PastSection past={past} open={pastOpen} onToggle={onPastToggle} confirmDel={confirmDel} onReuse={onReuse} onDelete={onDelete} onNo={onNo} onYes={onYes} />
    </section>
  );
}
